import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles, Mail, Check } from 'lucide-react';
import type { PendingJob } from '../hooks/useAgentChat';

export interface PendingJobCardProps {
  job: PendingJob;
  /** Narrow drawer vs full chat view; controls density. */
  compact?: boolean;
}

/**
 * Inline animation card for an in-flight ResearchJob (solution taxonomy,
 * tech discovery, etc.). Placed at the bottom of the message thread so the
 * user's eye lands on it after whatever the agent last said.
 *
 * Three visual layers to make progress feel alive without being noisy:
 * 1. A spinning icon (real motion).
 * 2. The worker's `current_step_label` as the primary line — updates live
 *    via `research_job_progress` SSE events.
 * 3. A progress bar if the job has multiple steps, plus an elapsed-time
 *    counter that ticks every second so there's always something moving
 *    even between worker updates.
 * 4. A rotating "distract" tagline below the step label, so the user has
 *    something to read while waiting on a long step.
 */
export const PendingJobCard: React.FC<PendingJobCardProps> = ({ job, compact }) => {
  const elapsed = useElapsed(job.startedAt);
  const tagline = useRotatingTagline(job.kind, 4500);

  // Prefer the fine-grained diligence counter (tech-discovery step 5) when
  // the worker is reporting one — it's the real throughput signal. Fall
  // back to the coarse current_step/total_steps otherwise.
  const hasDiligence = (job.diligenceTotal ?? 0) > 0;
  const progressNumerator = hasDiligence
    ? (job.diligenceCompleted ?? 0)
    : job.currentStep;
  const progressDenominator = hasDiligence
    ? (job.diligenceTotal ?? 1)
    : job.totalSteps;

  const percent = clamp(
    progressDenominator > 0
      ? Math.round(((progressNumerator + 0.5) / progressDenominator) * 100)
      : 0,
    5,
    99,
  );

  return (
    <div
      className={[
        'relative overflow-hidden rounded-xl border border-blue-200 dark:border-blue-900/50',
        'bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-slate-900/60',
        compact ? 'px-3 py-2.5' : 'px-5 py-4',
      ].join(' ')}
    >
      {/* Shimmer overlay — pure CSS animation, no JS tick */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10 opacity-70 animate-shimmer"
      />

      <div className="relative flex items-start gap-3">
        <div className="flex-shrink-0 pt-0.5">
          <Loader2
            className={`${compact ? 'w-5 h-5' : 'w-6 h-6'} text-blue-600 dark:text-blue-400 animate-spin`}
            aria-hidden
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className={`font-medium text-slate-900 dark:text-slate-100 ${compact ? 'text-xs' : 'text-sm'}`}>
              {job.kindDisplay}
            </span>
            <span className={`flex-shrink-0 tabular-nums text-slate-500 dark:text-slate-400 ${compact ? 'text-[10px]' : 'text-xs'}`}>
              {elapsed}
            </span>
          </div>

          {/* Worker's authoritative step label. Subtle transition so
              label changes aren't jarring. */}
          <div
            key={job.currentStepLabel}
            className={`mt-0.5 font-semibold text-blue-800 dark:text-blue-200 animate-fade-in ${compact ? 'text-[13px]' : 'text-base'}`}
          >
            {job.currentStepLabel || 'Preparing…'}
          </div>

          {/* Rotating flavor text — gives the eye something new to read
              every few seconds without requiring real worker updates. */}
          <div className={`mt-1 text-slate-600 dark:text-slate-400 ${compact ? 'text-[11px]' : 'text-xs'}`}>
            <Sparkles className="inline-block w-3 h-3 mr-1 opacity-60" />
            <span key={tagline} className="animate-fade-in">{tagline}</span>
          </div>

          {/* Progress bar — fills smoothly between step boundaries */}
          {progressDenominator > 1 && (
            <div className="mt-2 flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-900/40">
                <div
                  className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-700 ease-out"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className={`tabular-nums text-slate-500 dark:text-slate-400 flex-shrink-0 ${compact ? 'text-[10px]' : 'text-xs'}`}>
                {progressNumerator}/{progressDenominator}
                {hasDiligence ? ' tasks' : ''}
              </span>
            </div>
          )}

          {/* Email-when-done: nonfunctional placeholder. Flips to a
              confirmation state on click so users can step away without
              watching the card. Wire to a real notification backend later. */}
          <EmailWhenDone compact={compact} />
        </div>
      </div>
    </div>
  );
};

const EmailWhenDone: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const [armed, setArmed] = useState(false);
  const base = compact
    ? 'mt-2 inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md'
    : 'mt-3 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md';
  if (armed) {
    return (
      <div
        className={`${base} bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200`}
      >
        <Check className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
        <span>Got it — we'll email you when this finishes</span>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setArmed(true)}
      className={`${base} bg-white/70 dark:bg-slate-800/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-white dark:hover:bg-slate-800 transition-colors`}
    >
      <Mail className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      <span>Email me when done</span>
    </button>
  );
};

// ── helpers ─────────────────────────────────────────────────────────────────

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const start = startedAt ? new Date(startedAt).getTime() : now;
  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  return `${minutes}m ${remSec}s`;
}

const TAGLINES: Record<string, string[]> = {
  solution_taxonomy: [
    'Scanning the solution space for relevant families…',
    'Cross-referencing open-source and commercial offerings…',
    'Weighing maturity against risk for each approach…',
    'Drafting a structured comparison for you…',
    'Surfacing trade-offs worth reviewing…',
  ],
  tech_discovery: [
    'Walking the market for active vendors…',
    'Pulling funding, team, and contract history…',
    'Checking SAM.gov registrations…',
    'Reading product pages and spec sheets…',
    'Clustering findings into candidate shortlists…',
  ],
  _default: [
    'Working through this — hang tight…',
    'Gathering what we know so far…',
    'Checking a few places for fresh data…',
    'Stitching the pieces together…',
  ],
};

function useRotatingTagline(kind: string, intervalMs: number): string {
  const pool = TAGLINES[kind] || TAGLINES._default;
  const [index, setIndex] = useState<number>(() =>
    Math.floor(Math.random() * pool.length),
  );
  const poolRef = useRef(pool);
  poolRef.current = pool;

  useEffect(() => {
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % poolRef.current.length);
    }, intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);

  return pool[index % pool.length];
}

export default PendingJobCard;
